import {BookingModel} from "../booking.model";
import {IPassengerType} from "../../../passenger-types/passengers-types.service.interface";
import {
    DotRezPassengerAddressStatusApi,
    DotRezPassengerAddressStatusGraphQL,
    IDotRezBookingPassenger,
    IDotRezPassengerAddressGraphQL,
    IDotRezPassengerInfo,
    IDotRezPassengerName,
    IDotRezPassengerTravelDocument
} from "../../../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {InfantPassengerModel} from "./infant-passenger.model";
import {IMaturePassengerViewModel} from "./mature-passenger-view-model.interface";
import {computed, makeObservable, observable, runInAction} from "mobx";
import {
    IDotRezUpdatePassengerDetailsRequest
} from "../../../dot-rez-api/data-contracts/requests/booking/update-passenger-details.request";
import {Price} from "../../../currency/price";
import {PassengerSegmentModel} from "../passenger-segment/passenger-segment.model";
import {PassengerModelBase} from "./passenger-model-base";
import {ICheckInRestriction} from "../check-in-restrictions/check-in-restriction.interface";
import {TravelDocumentValidationRestriction} from "../check-in-restrictions/travel-document-validation-restriction";
import {SegmentModel} from "../segment/segment.model";
import {PassengerSegmentSsrEditorModel} from "../ssrs/passenger-segment-ssr-editor.model";
import {ISsrTypeCount} from "../ssrs/ssr-type-count.interface";
import {FeeTypeEnum} from "../../../dot-rez-api/data-contracts/enums/fee-type.enum";
import {NullableString} from "../../../../types/nullable-types";
import {FormFields, IFormField} from "../../../../models/forms/form-field.interface";
import {IPassengerInfoFields} from "./passenger-info-fields.interface";
import {PassengerFeeModel} from "../fees/passenger/passenger-fee.model";
import {PassengerNameChangeMutation} from "../mutation-actions/name-change/passenger-name-change.mutation";
import {JourneyModel} from "../journey/journey.model";
import {BookingSessionStorageKeys} from "../storage/booking-session-storage-keys";
import {IActivateErrorsValidationOptions} from "../../../../models/forms/form-model.interface";
import {
    getRelatedPassengerTypes,
    PassengerTypeCodesEnum
} from "../../../passenger-types/passenger-type-codes.enum";
import {IPassengerTypeMutationDescriptor} from "./passenger-view-model.interface";
import {MaturePassengerSessionStorage} from "./mature-passenger-session-storage";
import {MaturePassengerSessionStorageKeys} from "./mature-passenger-session-storage-keys";
import {MaturePassengerTypeMutationDescriptor} from "./mature-passenger-type-mutation-descriptor";
import {
    IDotRezChangePassengerTypeResponse
} from "../../../dot-rez-api/data-contracts/responses/booking/change-passenger-type.response";

const EXTRA_SEAT_SUFFIX = "EXST";
export class MaturePassengerModel extends PassengerModelBase implements IMaturePassengerViewModel {
    constructor(passengerKey: string,
                public readonly passengerIndex: number,
                booking: BookingModel) {
        super(passengerKey, booking);

        this.storage = new MaturePassengerSessionStorage(this);

        this._isPrimaryContact = this.booking.storage.getItem(BookingSessionStorageKeys.primaryContact) === this.stablePassengerUniqueKey;

        this._passengerKeysHistory.push(this.passengerKey);

        if(this.data.infant) {
            this._infant = new InfantPassengerModel(this, () => this.data, () => this.dataOnPreviousSession);
        }

        const originalPassengerTypeCode = this.storage.getItem(MaturePassengerSessionStorageKeys.originalTypeCode);
        if(!originalPassengerTypeCode) {
            this.storage.setItem(MaturePassengerSessionStorageKeys.originalTypeCode, this.passengerType.code);
        }
        this._scheduledTypeChange = this.storage.getItem(MaturePassengerSessionStorageKeys.scheduledTypeCodeChange) as PassengerTypeCodesEnum || null;

        makeObservable<this, '_isPrimaryContact' | '_scheduledTypeChange'>(this, {
            _isPrimaryContact: observable.ref,
            _scheduledTypeChange: observable.ref,
            fees: computed,
            passengerTypeIndex: computed,
            filteredPassengerSegments: computed,
            unfilteredPassengerSegments: computed,
            hasPerformedCheckInOnAnySegment: computed
        });
    }

    get discountCode(): string {
        return this.data.discountCode;
    }

    private readonly _infant: InfantPassengerModel | null = null;
    readonly storage: MaturePassengerSessionStorage;

    get infant(): InfantPassengerModel | null {
        return this._infant;
    }

    private get dataOnPreviousSession(): IDotRezBookingPassenger | null {
        if(!this.booking.initialBookingSnapshot) {
            return null;
        }

        for(let passengerData of this.booking.initialBookingSnapshot.bookingData.passengers) {
            if(passengerData.key === this.passengerKey) {
                return passengerData.value;
            }
        }
        return null;
    }

    matchPassengerKey(passengerKey: string): boolean {
        return this.passengerKey === passengerKey
                || this.stablePassengerUniqueKey === passengerKey
                || this._passengerKeysHistory.includes(passengerKey);
    }

    private get data(): IDotRezBookingPassenger {

        const passengerData = this.booking.bookingData.passengers[this.passengerIndex]?.value;
        if(passengerData) {
            return passengerData;
        }

        throw new Error(`There is no passenger data for passenger with index ${this.passengerIndex}`);
    }

    get passengerType(): IPassengerType {
        return this.booking.services.passengerTypes.getPassengerType(this.data.passengerTypeCode);
    }

    get isDogPlaceholder(): boolean {
        if(this.passengerType.isDog) {
            return false;
        }

        if(!this.passengerType.isAdult) {
            return false;
        }

        if(this.booking.passengers.countDogs() === 0) {
            return false;
        }

        if(this.data.name?.suffix === EXTRA_SEAT_SUFFIX) {
            return true;
        }

        const countPassengersWithTheSameType = this.booking.passengers
            .filter(p => p.passengerType.description === this.passengerType.description)
            .length;

        return this.passengerTypeIndex === countPassengersWithTheSameType - 1;
    }

    get passengerTypeIndex(): number {
        return this.booking.passengers
                .filter(p => p.passengerType.description === this.passengerType.description)
                .findIndex(p => p.passengerKey === this.passengerKey);
    }

    get canHaveSpecialAssistance(): boolean {
        return true;
    }

    private _isPrimaryContact: boolean;
    get isPrimaryContact(): boolean {
        if(this.booking.shouldSyncContactWithPrimaryPassenger) {
            return this._isPrimaryContact;
        }

        return false;
    }

    set isPrimaryContact(value: boolean) {
        if(!this.booking.shouldSyncContactWithPrimaryPassenger) {
            return;
        }

        if(this.isPrimaryContact === value) {
            return;
        }

        if(value) {
            const currentPrimaryContactPassenger = this.booking.passengers.find(p => p.passengerKey !== this.passengerKey && p.isPrimaryContact);
            if(currentPrimaryContactPassenger) {
                currentPrimaryContactPassenger.isPrimaryContact = false;
            }

            this.booking.storage.setItem(BookingSessionStorageKeys.primaryContact, this.stablePassengerUniqueKey);

            this.booking.contact.fields.firstName.setValue(this.fields.firstName.value);
            this.booking.contact.fields.lastName.setValue(this.fields.lastName.value);
            this.booking.contact.fields.phoneNumber.setValue(this.fields.phoneNumber.value);
            this.booking.contact.fields.emailAddress.setValue(this.fields.emailAddress.value);

        } else if(this.isPrimaryContact) {
            this.booking.storage.setItem(BookingSessionStorageKeys.primaryContact, 'none');
        }

        runInAction(() => {
            this._isPrimaryContact = value;
        });
    }

    get nameOnPreviousSession(): IDotRezPassengerName | null {
        return this.dataOnPreviousSession?.name ?? null;
    }

    get name(): IDotRezPassengerName {
        if(!this.data.name) {
            this.data.name =  {
                title: null,
                first: null,
                last: null
            }
        }
        return this.data.name;
    }

    get infoOnPreviousSession(): IDotRezPassengerInfo | null {
        return this.dataOnPreviousSession?.info ?? null;
    }


    get info(): IDotRezPassengerInfo {
        return this.data.info;
    }

    get travelDocuments(): IDotRezPassengerTravelDocument[] {
        if(!this.data.travelDocuments) {
            runInAction(() => {
                this.data.travelDocuments = [];
            });
        }
        return this.data.travelDocuments;
    }

    get addressesOnPreviousSession(): IDotRezPassengerAddressGraphQL[] {
        return this.dataOnPreviousSession?.addresses ?? [];
    }

    get addresses(): IDotRezPassengerAddressGraphQL[] {
        if (this.data.addresses) {
            return this.data.addresses;
        }
        return [];
    }

    private _extractPassengersSegmentsFromJourneys(journeys: JourneyModel[]) : PassengerSegmentModel[] {
        const result: PassengerSegmentModel[] = [];

        for(let journey of journeys) {
            for(let segment of journey.segments) {
                for(let passengerSegment of segment.passengers) {
                    if(passengerSegment.passenger.passengerKey === this.passengerKey) {
                        result.push(passengerSegment);
                    }
                }
            }
        }

        return result;
    }

    get filteredPassengerSegments(): PassengerSegmentModel[] {
        return this._extractPassengersSegmentsFromJourneys(this.booking.filteredJourneys);
    }


    get unfilteredPassengerSegments(): PassengerSegmentModel[] {
        return this._extractPassengersSegmentsFromJourneys(this.booking.unfilteredJourneys);
    }

    get hasPerformedCheckInOnAnySegment(): boolean {
        return this.unfilteredPassengerSegments.some(passengerSegment => passengerSegment.isCheckedIn);
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        if(this.isPrimaryContact) {
            switch (field.fieldName) {
                case this.fields.firstName.fieldName:
                    this.booking.contact.fields.firstName.setValue(this.fields.firstName.value);
                    break;
                case this.fields.lastName.fieldName:
                    this.booking.contact.fields.lastName.setValue(this.fields.lastName.value);
                    break;
                case this.fields.phoneNumber.fieldName:
                    this.booking.contact.fields.phoneNumber.setValue(this.fields.phoneNumber.value);
                    break;
                case this.fields.emailAddress.fieldName:
                    this.booking.contact.fields.emailAddress.setValue(this.fields.emailAddress.value);
                    break;
            }
        }
    }


    commitChanges() {
        super.commitChanges();

        runInAction(() => {
            if(!this.hasPerformedCheckInOnAnySegment) {
                this.name.first = this.fields.firstName.value?.toUpperCase()?.trim() || null;
                this.name.last = this.fields.lastName.value?.toUpperCase()?.trim() || null;
                this.name.title = this.computeTitle();
                this.data.customerNumber = this.fields.customerNumber.value;
            }

            this.data.info.dateOfBirth = this.services.time.formatBirthDate(this.fields.dateOfBirth.value) || null;
            this.data.info.gender = this.fields.gender.value;
            this.data.info.nationality = this.fields.nationality.value;
        });
    }

    activateErrorsValidation(options?: IActivateErrorsValidationOptions): IFormField[] {
        const errors = super.activateErrorsValidation(options);

        if(this.requirePsoDetails) {
            return [
                ...errors,
                ...this.travelDocument.activateErrorsValidation(options)
            ];
        }

        return errors;
    }

    private async _updatePassengerDetails(): Promise<void> {
        if(!this.hasChanges()) {
            return;
        }

        const updatePassengerRequest: Partial<IDotRezUpdatePassengerDetailsRequest> = {
            info: {
                gender: this.fields.gender.value,
                nationality: this.fields.nationality.value,
                dateOfBirth: this.services.time.formatBirthDate(this.fields.dateOfBirth.value) || null
            }
        }

        if(!this.hasPerformedCheckInOnAnySegment) {
            updatePassengerRequest.customerNumber = this.customerNumber;
            updatePassengerRequest.discountCode = this.discountCode;

            if(this.isDogPlaceholder) {
                updatePassengerRequest.name = {
                    title: this.booking.passengers[0].computeTitle(),
                    last: this.booking.passengers[0].fields.lastName.value,
                    first: this.booking.passengers[0].fields.firstName.value,
                    suffix: EXTRA_SEAT_SUFFIX
                };
            } else {
                updatePassengerRequest.name = {
                    title: this.computeTitle(),
                    last: this.fields.lastName.value?.toUpperCase()?.trim() || null,
                    first: this.fields.firstName.value?.toUpperCase()?.trim() || null,
                };
            }
        }

        await this.booking.session.updatePassengerDetails(this.passengerKey, updatePassengerRequest);

        if(this.requirePsoDetails || this.services.configuration.isAgencyPortal) {
            await this.saveAddress();
        }

        this.commitChanges();
    }

    async savePassengerDetails(): Promise<void> {
        await this._updatePassengerDetails();

        if(this.requirePsoDetails) {
            await this.travelDocument.saveTravelDocument();
        }
    }

    async saveAddress(): Promise<void> {
        if(this.addresses.length === 0) {

            const addressKey = await this.booking.session.addPassengerAddress(this.passengerKey, {
                status: DotRezPassengerAddressStatusApi.Contact,
                city: this.fields.addressCity.value,
                lineThree: this.fields.psoPassengerType.value,
                phone: this.fields.phoneNumber.value,
                emailAddress: this.fields.emailAddress.value,

            });
            runInAction(() => {
                this.addresses.push({
                    passengerAddressKey: addressKey,
                    city: this.fields.addressCity.value,
                    status: DotRezPassengerAddressStatusGraphQL.Contact,
                    phone: this.fields.phoneNumber.value,
                    emailAddress: this.fields.emailAddress.value,
                });
            })
        } else {
            await this.booking.session.updatePassengerAddress(this.passengerKey, {
                passengerAddressKey: this.addresses[0].passengerAddressKey,
                city: this.fields.addressCity.value,
                lineThree: this.fields.psoPassengerType.value,
                phone: this.fields.phoneNumber.value,
                emailAddress: this.fields.emailAddress.value,
            });
        }
    }

    async saveTravelDocument(travelDocument: IDotRezPassengerTravelDocument): Promise<NullableString> {

        await this._updatePassengerDetails();

        if(this.isOnDomesticFlight && !this.requirePsoDetails) {
            return null;
        }

        let saveTravelDocRequest: Partial<IDotRezPassengerTravelDocument> = travelDocument;
        if(this.hasPerformedCheckInOnAnySegment) {
            saveTravelDocRequest = {
                ...saveTravelDocRequest,
                name: this.name // if the  passenger is checked in we keep the same values for the name for the document because dotREZ doesn't allow changing this for checked in passengers
            }
        }

        const {passengerTravelDocumentKey} = await this.booking.session.savePassengerTravelDocument(this.passengerKey, saveTravelDocRequest);
        return passengerTravelDocumentKey;
    }

    getUserDependentCheckInBlockingRestrictions(): ICheckInRestriction[] {
        const restrictions: ICheckInRestriction[] = [];
        const language = this.services.language;
        if(this.travelDocument.hasErrors()) {
            restrictions.push(new TravelDocumentValidationRestriction(language.translate('Invalid travel document information')));
        }

        if(this.infant?.travelDocument.hasErrors()) {
            restrictions.push(new TravelDocumentValidationRestriction(language.translationFor('Infant passenger {infantName} assigned to this passenger has invalid travel document information').withParams({infantName: this.infant.getFullName()})));
        }
        return restrictions;
    }

    getUserIndependentCheckInBlockingRestrictions(): ICheckInRestriction[] {
        return [];
    }

    get fees(): PassengerFeeModel[] {
        return this.data.fees.map(f => new PassengerFeeModel(f, this));
    }

    getSpoilageFee(): PassengerFeeModel | null {
        return this.fees.find(f => f.feeType === FeeTypeEnum.SpoilageFee) || null;
    }

    getBundleFee(segment: SegmentModel): Price {
        const fee = this.fees.find(f => f.feeType === FeeTypeEnum.ServiceBundle && (f.flightReference === segment.flightReference));
        if(fee) {
            return fee.totalToDisplay;
        }
        return this.booking.createPrice(0);
    }

    getSsrsCountForJourney(journeyKey: string): ISsrTypeCount[] {
        const journey = this.booking.filteredJourneys.find(j => j.journeyKey === journeyKey);
        if(!journey) {
            return [];
        }

        let journeySsrsCount: ISsrTypeCount[] = [];
        const passengerSegments = journey.getAllPassengersSegments().filter(ps => ps.passenger.passengerKey === this.passengerKey);

        const allPassengerJourneySsrsGroupedBySsrCode = passengerSegments.reduce((allSsr: PassengerSegmentSsrEditorModel[], passengerSegment) =>
                                        allSsr.concat(passengerSegment.getSoldSsrsEditors()), [])
            .groupByKey(passengerSegmentSsr => passengerSegmentSsr.ssrType.ssrCode);


        Object.keys(allPassengerJourneySsrsGroupedBySsrCode).forEach(ssrCode => {
            const ssrItems = allPassengerJourneySsrsGroupedBySsrCode[ssrCode];

            if(ssrItems[0].ssrType.shouldBeSoldPerSegment) {
                journeySsrsCount.push({
                    ssrType: ssrItems[0].ssrType,
                    count: ssrItems.sum(item => item.newQuantityToDisplay)
                });
            } else {
                journeySsrsCount.push({
                    ssrType: ssrItems[0].ssrType,
                    count: ssrItems[0].newQuantityToDisplay
                });
            }
        });

        journeySsrsCount = journeySsrsCount.sort((s1, s2) => {
            if(s1.ssrType.preferredOrderInLists < s2.ssrType.preferredOrderInLists) {
                return -1
            }
            return 1;
        });

        return this.services.ssrTypes.filterVisibleSsrs(journeySsrsCount);
    }

    protected _getInitialCustomerNumber(): NullableString {
        return this.data.customerNumber;
    }

    protected _onFieldsCreated(fields: FormFields<IPassengerInfoFields>) {
        super._onFieldsCreated(fields);
        fields.firstName.onChange(value => {
            if(this.isPrimaryContact) {
                this.booking.contact.fields.firstName.setValue(value);
            }
        });

        fields.lastName.onChange(value => {
            if(this.isPrimaryContact) {
                this.booking.contact.fields.lastName.setValue(value);
            }
        })

        fields.emailAddress.onChange(value => {
            if(this.isPrimaryContact) {
                this.booking.contact.fields.emailAddress.setValue(value);
            }
        })

        fields.phoneNumber.onChange(value => {
            if(this.isPrimaryContact) {
                this.booking.contact.fields.phoneNumber.setValue(value);
            }
        })

    }

    markAsBlueBenefitsPassenger(): void  {
        this.companion.attachCompanion(this.services.user.profile.asCompanion());
    }

    get nameChangeBlockingReason(): NullableString {
        if(this.booking.passengersNamesChangeBlockingReason) {
            return this.booking.passengersNamesChangeBlockingReason;
        }

        if(this.booking.blueBenefits.isBookingWithBlueBenefits && this.passengerIndex === 0) {
            return this.services.language.translate('Name cannot be changed for the BlueBenefits subscription holder!');
        }

        if (this.hasPerformedCheckInOnAnySegment) {
            return this.services.language.translate('Name cannot be changed for checked-in in passengers!');
        }

        return null;
    }

    protected _startNameChangeMutation() {
        this.booking.mutationsManager.startMutation(new PassengerNameChangeMutation(this, {
            onCompleted: () => {
                this.companion.detachCompanion();
                this.commitChanges();
            }
        }));
    }

    private _scheduledTypeChange: PassengerTypeCodesEnum | null = null;
    get scheduledPassengerTypeChange(): PassengerTypeCodesEnum | null {
        return this._scheduledTypeChange;
    }

    set scheduledPassengerTypeChange(value: PassengerTypeCodesEnum | null) {
        runInAction(() => {
            this._scheduledTypeChange = value;
            if(value) {
                this.storage.setItem(MaturePassengerSessionStorageKeys.scheduledTypeCodeChange, value)
            } else {
                this.storage.removeItem(MaturePassengerSessionStorageKeys.scheduledTypeCodeChange)
            }
        });
    }

    get originalPassengerTypeCode(): PassengerTypeCodesEnum {
        return this.storage.getItem(MaturePassengerSessionStorageKeys.originalTypeCode) as PassengerTypeCodesEnum;
    }


    revertToOriginalPassengerType(): void {
        this.scheduledPassengerTypeChange = this.originalPassengerTypeCode;
    }

    async changePassengerType(newPassengerTypeCode: PassengerTypeCodesEnum): Promise<void> {

        if(newPassengerTypeCode === this.passengerType.code) {
            return;
        }

        const passengerTypeBefore = this.data.passengerTypeCode;

        let result: IDotRezChangePassengerTypeResponse
        try {
            runInAction(() => {
                this.data.passengerTypeCode = newPassengerTypeCode;
            });

            result = await this.booking.session.changePassengerType({
                passengerKey: this.passengerKey,
                newPassengerTypeCode: newPassengerTypeCode
            });

        } catch(err) {
            runInAction(() => {
                this.data.passengerTypeCode = passengerTypeBefore;
            });
            throw err;
        }

        runInAction(() => {
            this._passengerKeysHistory.push(result.newPassengerKey);
            this.data.passengerTypeCode = newPassengerTypeCode;
            super.onPassengerKeyChanged(result.newPassengerKey);
            this.infant?.onParentKeyChanged(result.newPassengerKey);
        });


    }

    get requirePsoDetails(): boolean {
        /*
        if(!this.services.configuration.isProduction && localStorage.getItem('require-pso-details') === 'false') {
            return false;
        }
         */

        return this.booking.pso.isEligibleForPsoMarketDiscount || this._getPsoPossiblePassengerTypeChanges().length > 0;
    }

    get allowPsoReducedMobility(): boolean {
        return this.booking.pso.allowReducedMobility && this.passengerType.isAdult;
    }


    get allowedPassengerTypeMutations(): IPassengerTypeMutationDescriptor[] {
        if(!this.passengerType.isAdult) {
            return []
        }

        return this._getPsoPossiblePassengerTypeChanges();

    }


    private _getPsoPossiblePassengerTypeChanges(): IPassengerTypeMutationDescriptor[] {
        const result: IPassengerTypeMutationDescriptor[] = [];

        if(this.booking.pso.allowMedicalReason) {
            result.push(new MaturePassengerTypeMutationDescriptor(this,
                                                                  PassengerTypeCodesEnum.AdultWithMedicalReason,
                                                                  getRelatedPassengerTypes(PassengerTypeCodesEnum.AdultWithMedicalReason),
                                                                  () => this.services.language.translate('Travel for medical reason')));

        }

        if(this.booking.pso.allowStudent) {
            result.push(new MaturePassengerTypeMutationDescriptor(this,
                                                                  PassengerTypeCodesEnum.Student,
                                                                  getRelatedPassengerTypes(PassengerTypeCodesEnum.Student),
                                                                  () => this.services.language.translate('Student')));

        }

        if(this.booking.pso.allowRetired) {
            result.push(new MaturePassengerTypeMutationDescriptor(this,
                                                                  PassengerTypeCodesEnum.AdultRetired,
                                                                  getRelatedPassengerTypes(PassengerTypeCodesEnum.AdultRetired),
                                                                  () => this.services.language.translate('Retired')));
        }

        return result;
    }


}
